// SentItemIcon — the row-kind glyph, drawn from the WHP design-system icon set.
//
// WHY THE GEOMETRY IS HERE RATHER THAN A `<use href>` INTO icons.svg. T29 asks
// for the sprite instead of emoji or ad-hoc SVG, and the glyphs below ARE the
// sprite's — copied symbol-for-symbol out of
// src/design-system/assets/icons.svg, same 24×24 box, same 1.5px stroke, same
// `currentColor`. What is deliberately not copied is the *delivery*: an
// external `<use href="…icons.svg#file">` fails silently — no console error,
// no fallback, just an empty box — wherever a browser declines to follow the
// cross-document reference, and the alternative (inlining the whole 56-symbol
// sprite into the document) makes every page that mounts SentItemList
// responsible for injecting it, which is exactly the kind of invisible
// dependency W6 would inherit and trip over.
//
// So: the design system's glyphs, delivered as markup this component owns. If
// the sprite's geometry changes, these change with it — they are quotations,
// not new drawings, and anything not in the curated set must be escalated
// rather than invented here.

import type { SentItemGlyph } from '@/components/sentItems/sentItemsView';

/** Verbatim from design-system/assets/icons.svg — one entry per symbol used. */
const GLYPHS: Record<SentItemGlyph, React.JSX.Element> = {
  file: (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </>
  ),
  users: (
    <>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </>
  ),
  check: <polyline points="20 6 9 17 4 12" />,
  home: (
    <>
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </>
  ),
  key: (
    <>
      <circle cx="7.5" cy="15.5" r="4.5" />
      <path d="M10.85 12.15 21 2" />
      <path d="m18 5 3 3" />
      <path d="m15 8 3 3" />
    </>
  ),
  banknote: (
    <>
      <rect x="2" y="6" width="20" height="12" rx="2" />
      <circle cx="12" cy="12" r="2.5" />
      <path d="M6 12h.01" />
      <path d="M18 12h.01" />
    </>
  ),
  dollar: (
    <>
      <line x1="12" y1="2" x2="12" y2="22" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </>
  ),
  attach: (
    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66L9.41 17.41a2 2 0 0 1-2.83-2.83l8.49-8.49" />
  ),
  flag: (
    <>
      <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
      <line x1="4" y1="22" x2="4" y2="15" />
    </>
  ),
  user: (
    <>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </>
  ),
  calendar: (
    <>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </>
  ),
};

/**
 * A decorative kind marker. It is `aria-hidden` on purpose: the kind is
 * already written beside it as a pill, and a screen reader announcing "key,
 * Lease" says the same thing twice.
 */
export function SentItemIcon({
  glyph,
  size = 16,
}: {
  glyph: SentItemGlyph;
  size?: number;
}): React.JSX.Element {
  return (
    <svg
      className="si-icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      data-glyph={glyph}
    >
      {GLYPHS[glyph]}
    </svg>
  );
}
